<template>
  <div class="flex justify-center items-center min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
    <div class="relative w-full max-w-lg p-1 rounded-box shadow-box">
      <!-- Background Gradient Border -->
      <div class="absolute inset-0 bg-gradient-to-r from-[#C2ED5C] to-[#63CCEB] rounded-box rounded-[2rem] z-0"></div>

      <div class="relative bg-black p-8 rounded-box rounded-[2rem] z-10">
        <!-- Logo -->
        <div class="text-center mb-6">
          <img :alt="$config.client.name" :src="logoPath" class="h-16 w-auto mx-auto"/>
        </div>

        <!-- External Auth -->
        <div v-if="externalAuth.enabled" class="mb-6">
          <h5 class="text-center font-bold text-white mb-4 uppercase text-sm tracking-wider">
            {{ externalAuth.label }}
          </h5>
          <button
            class="w-full py-2 px-4 bg-gradient-to-r from-[#C2ED5C] to-[#63CCEB] hover:from-[#63CCEB] hover:to-[#C2ED5C] text-black font-bold rounded-lg transition-all duration-200 transform hover:scale-[1.02]"
            data-test="signIn-submitExternalAuth"
            @click.prevent="submitExternalAuth"
          >
            {{ $t('words.login') }}
          </button>
        </div>

        <!-- ProConnect -->
        <div v-if="proConnectAuth.enabled" class="text-center mb-6">
          <form @submit.prevent>
            <button class="proconnect-button mx-auto block" @click="proconnectSigninRedirect">
              <span class="proconnect-sr-only">{{ $t('signin.proconnectlogin') }}</span>
            </button>
          </form>
          <p class="mt-2">
            <a
              href="https://www.proconnect.gouv.fr/"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white text-xs hover:text-gray-300 transition-colors"
              :title="$t('whatIsProConnect') + ' - nouvelle fenêtre'"
            >
              {{ $t('signin.proconnectdoc')}}
              <b-icon-box-arrow-up-right class="ml-1" />
            </a>
          </p>
        </div>

        <!-- Divider -->
        <div v-if="externalAuth.enabled || proConnectAuth.enabled" class="flex items-center my-8">
          <div class="flex-grow border-t border-gray-700"></div>
          <span class="px-4 text-gray-500 text-xs uppercase tracking-widest font-bold">
            {{ $t('signin.divider') }}
          </span>
          <div class="flex-grow border-t border-gray-700"></div>
        </div>

        <!-- Main Title -->
        <h2 v-if="$config.forms.signin.title" class="text-center font-bold text-white mb-8 uppercase tracking-wide text-xl">
          {{ $config.forms.signin.title[$i18n.locale] }}
        </h2>
        <h2 v-else class="text-center font-bold text-white mb-8 uppercase tracking-wide">
          {{ $t('signin.title', { org: $config.client.name }) }}
        </h2>

        <!-- Login Form -->
        <form class="space-y-6" @submit.prevent>
          <!-- Username -->
          <div>
            <label for="username" class="block text-white mb-2 text-xs font-bold uppercase tracking-wider">
              {{ $t('words.username') }}
            </label>
            <input
              v-model="form.username"
              id="username"
              type="text"
              class="w-full px-4 py-3 bg-white text-black rounded-xl focus:outline-none focus:ring-2 focus:ring-[#63CCEB] transition-shadow shadow-sm"
              data-test="signIn-username"
              v-on:keydown.enter.prevent="submit"
              required
            >
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-white mb-2 text-xs font-bold uppercase tracking-wider">
              {{ $t('words.password') }}
            </label>
            <div class="relative">
              <input
                v-model="form.password"
                id="password"
                :type="showPassword ? 'text' : 'password'"
                class="w-full px-4 py-3 bg-white text-black rounded-xl focus:outline-none focus:ring-2 focus:ring-[#63CCEB] transition-shadow shadow-sm"
                data-test="signIn-password"
                v-on:keydown.enter.prevent="submit"
                required
              >
              <button
                type="button"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black transition-colors"
                @click="showPassword = !showPassword"
              >
                <b-icon :icon="showPassword?'eye-slash-fill':'eye-fill'" :aria-label="$t('ariaLabels.displayPassword')" />
              </button>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="bg-red-900/30 border border-red-500 text-red-400 px-4 py-2 rounded-lg text-sm italic">
            {{ error }}
          </div>

          <!-- Forgotten Password -->
          <div class="text-right">
            <router-link :to="{ name: 'ForgottenPassword' }" class="text-white text-[10px] uppercase font-bold tracking-tighter hover:text-gray-300 transition-colors">
              {{ $t('signin.forgottenpassword') }} ?
            </router-link>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            :disabled="(!form.username || !form.password) || btnPressed"
            class="w-full py-2 px-4 bg-gradient-to-r from-[#C2ED5C] to-[#63CCEB] hover:from-[#63CCEB] hover:to-[#C2ED5C] disabled:cursor-not-allowed text-black font-bold uppercase text-2xl rounded-full transition-all duration-200 shadow-lg transform active:scale-95"
            data-test="signIn-submit"
            @click.prevent="submit"
          >
            {{ $t('words.login') }}
          </button>

          <!-- Warning message -->
          <div
            v-if="$config.client.warning && $config.client.name && $config.client.email"
            class="flex items-center space-x-4 bg-gray-900/50 p-4 rounded-xl border border-gray-800"
          >
            <img src="@/assets/attention_image.jpg" class="h-10 w-auto flex-shrink-0" />
            <p class="text-red-400 text-[10px] leading-tight">
              {{ $t('signin.warning', { org: $config.client.name }) }}
              <a :href="`mailto:${$config.client.email}`" class="underline hover:text-red-300">{{ $config.client.email }}</a>
            </p>
          </div>

          <!-- Sign Up -->
          <div v-if="!$config.forms.signup.disabled" class="text-center mt-8">
            <router-link :to="{ name: 'SignUp' }" class="text-white text-xs font-bold uppercase tracking-wider hover:text-gray-300 transition-colors">
              {{ $t('signin.noaccount') }} ?
            </router-link>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer 
    <small class="absolute bottom-6 text-gray-400 text-[10px] tracking-widest uppercase">
      <p>{{ $t('footer') }} <a href="https://www.neogeo.fr/" target="_blank" rel="noopener" class="hover:text-black transition-colors">Neogeo-Technologies</a></p>
    </small>
    -->
  </div>
</template>

<script>

import loginAPI from '@/api/loginAPI.js';



export default {
  name: 'SignIn',

  data() {
    return {
      error:null,
      next: null,
      nextForwarded: null,
      externalAuth: {
        enabled: process.env.VUE_APP_EXTERNAL_LOGIN === 'true',
        url: process.env.VUE_APP_EXTERNAL_LOGIN_URL,
        label: process.env.VUE_APP_EXTERNAL_LOGIN_LABEL
      },

      proConnectAuth: {
        enabled: process.env.VUE_APP_PROCONNECT_LOGIN === 'true',
        url: process.env.VUE_APP_PROCONNECT_LOGIN_URL
      },

      showPassword: false,
      btnPressed: false,
      form: {
        username: null,
        password: null
      }
    };
  },

  computed: {
    logoPath() {
      return require(process.env.VUE_APP_LOGO);
    }
  },

  created() {
    this.next=this.$route.query.next || process.env.VUE_APP_NEXT_DEFAULT;
    this.nextForwarded=this.$route.query.nextForwarded;
  },
  mounted() {
    if (loginAPI.logged) {
      this.logged();
    }
  },

  methods: {
    logged() {
      if (loginAPI.logged && this.next) {
        if (
          this.$config.authorizedRedirections.some(reg => {
            return reg.test(decodeURIComponent(this.next))
          })
        ) {
          if (this.nextForwarded) {
            // Call from RP, IDP return to RP with JWT token
            window.location.href = this.next + "?next=" + this.nextForwarded + "&jwttoken=" + loginAPI.token;
          } else {
            window.location.href = this.next;
          }
        } else {
          this.$router.push({ name: 'NotFound' });
        }
      }
    },
    async submit() {
      this.btnPressed = true;
     
      const data = {
        username: this.form.username,
        password: this.form.password,
      };
      loginAPI.signIn(data)
        .then(
          () => {
            this.logged();
          },
        )
        .catch(
          (error) => {
            if (error.response.status === 403) {
              this.error= error.response.data.detail
                || i18n.t('messages.loginError');
            
            }
          },
        );
    },
    submitExternalAuth() {
      window.location.href = this.externalAuth.url;
    },
    proconnectSigninRedirect() {
      window.location.href = this.proConnectAuth.url;
    }
  },
};
</script>

<style >
</style>
